import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';
import DeleteIconButton from '../components/DeleteIconButton';
import {
  invalidateClientSnapshot,
  invalidateRegionTeamsCache,
  suggestTeamName,
} from '../utils/clientManagement';

function AdminRegionPage() {
  const { clientId, regionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [clientName, setClientName] = useState(location?.state?.clientName || '');
  const [regionName, setRegionName] = useState(location?.state?.regionName || '');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingRegionName, setEditingRegionName] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  const [savingRegionName, setSavingRegionName] = useState(false);
  const [regionNameError, setRegionNameError] = useState('');

  const [showTeamModal, setShowTeamModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [teamModalError, setTeamModalError] = useState('');
  const [addingTeam, setAddingTeam] = useState(false);

  const [editingTeamId, setEditingTeamId] = useState(null);
  const [newTeamNameEdit, setNewTeamNameEdit] = useState('');
  const [savingTeamName, setSavingTeamName] = useState(false);
  const [teamNameError, setTeamNameError] = useState('');

  const [teamToDelete, setTeamToDelete] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(false);

  const loadTeams = async () => {
    const { data: teamsData, error: teamsErr } = await supabase
      .from('teams')
      .select('id, name, region_id, client_id, active')
      .eq('client_id', clientId)
      .eq('region_id', regionId)
      .eq('active', true)
      .order('name');

    if (teamsErr) throw new Error('Failed to load teams');

    const teamList = (teamsData || []).map((t) => ({ ...t, users: [] }));
    const teamIds = teamList.map((t) => t.id);

    if (teamIds.length > 0) {
      const { data: teamUsers } = await supabase
        .from('team_users')
        .select('team_id, user_id')
        .in('team_id', teamIds);

      const userIdSet = new Set((teamUsers || []).map((tu) => String(tu.user_id)));
      let usersById = {};
      if (userIdSet.size > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('id, full_name, email')
          .in('id', Array.from(userIdSet));
        (usersData || []).forEach((u) => {
          usersById[String(u.id)] = { id: String(u.id), full_name: u.full_name, email: u.email };
        });
      }

      (teamUsers || []).forEach((tu) => {
        const idx = teamList.findIndex((t) => t.id === tu.team_id);
        if (idx >= 0) {
          const u = usersById[String(tu.user_id)];
          if (u) (teamList[idx].users || (teamList[idx].users = [])).push(u);
        }
      });
    }

    return teamList;
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (!clientName) {
          const { data: clientData } = await supabase
            .from('clients')
            .select('business_name')
            .eq('id', clientId)
            .single();
          if (clientData?.business_name && mounted) setClientName(clientData.business_name);
        }

        const { data: regionData, error: regionErr } = await supabase
          .from('regions')
          .select('id, name, client_id')
          .eq('id', regionId)
          .single();
        if (regionErr || !regionData) {
          if (mounted) setError('Region not found');
          return;
        }
        if (String(regionData.client_id) !== String(clientId)) {
          if (mounted) setError('Region does not belong to this client');
          return;
        }
        if (mounted) setRegionName(regionData.name || location?.state?.regionName || '');

        const teamList = await loadTeams();
        if (!mounted) return;
        setTeams(teamList);
      } catch {
        if (mounted) setError('Failed to load region');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, regionId, location.key]);

  const handleSaveRegionName = async () => {
    const name = (newRegionName || '').trim();
    if (!name) {
      setRegionNameError('Region name is required');
      return;
    }
    try {
      setSavingRegionName(true);
      const { error: updErr } = await supabase
        .from('regions')
        .update({ name })
        .eq('id', regionId);
      setSavingRegionName(false);
      if (updErr) {
        setRegionNameError('Failed to rename region');
        return;
      }
      setRegionName(name);
      setEditingRegionName(false);
      invalidateClientSnapshot(clientId);
    } catch {
      setSavingRegionName(false);
      setRegionNameError('Unexpected error renaming region');
    }
  };

  const openAddTeamModal = async () => {
    setTeamModalError('');
    const suggested = await suggestTeamName(clientId, regionId, regionName || 'Region');
    setNewTeamName(suggested);
    setShowTeamModal(true);
  };

  const handleAddTeam = async () => {
    const name = (newTeamName || '').trim();
    if (!name) {
      setTeamModalError('Team name is required');
      return;
    }
    setAddingTeam(true);
    const { data, error: insertErr } = await supabase
      .from('teams')
      .insert({
        name,
        client_id: Number(clientId),
        region_id: Number(regionId),
        created_at: new Date().toISOString(),
      })
      .select('id, name, region_id, client_id')
      .single();
    setAddingTeam(false);
    if (insertErr) {
      setTeamModalError(insertErr.message || 'Failed to create team');
      return;
    }
    invalidateClientSnapshot(clientId);
    invalidateRegionTeamsCache(clientId, regionId);
    setShowTeamModal(false);
    navigate(`/team/${data.id}/edit-members`, {
      state: {
        clientId,
        teamName: data.name,
        team: { id: data.id, name: data.name, client_id: data.client_id, region_id: data.region_id },
      },
    });
  };

  const handleSaveTeamName = async (teamId) => {
    const name = (newTeamNameEdit || '').trim();
    if (!name) {
      setTeamNameError('Team name is required');
      return;
    }
    try {
      setSavingTeamName(true);
      const { error: updErr } = await supabase
        .from('teams')
        .update({ name })
        .eq('id', teamId);
      setSavingTeamName(false);
      if (updErr) {
        setTeamNameError('Failed to rename team');
        return;
      }
      setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, name } : t)));
      setEditingTeamId(null);
      invalidateRegionTeamsCache(clientId, regionId);
      invalidateClientSnapshot(clientId);
    } catch {
      setSavingTeamName(false);
      setTeamNameError('Unexpected error renaming team');
    }
  };

  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;
    setDeletingTeam(true);
    await supabase.from('teams').update({ active: false }).eq('id', teamToDelete);
    setTeams((prev) => prev.filter((t) => t.id !== teamToDelete));
    invalidateRegionTeamsCache(clientId, regionId);
    invalidateClientSnapshot(clientId);
    setTeamToDelete(null);
    setDeletingTeam(false);
  };

  return (
    <div className="dashboard-page">
      <RenderMonitor componentName="AdminRegionPage" />

      <button
        type="button"
        onClick={() => navigate(`/client/${clientId}`, {
          state: { clientName, refresh: Date.now() },
        })}
        className="modern-button modern-button-secondary modern-mb-6"
      >
        ← {clientName || 'Client'}
      </button>

      <div className="modern-card modern-mb-6">
        <div className="modern-flex modern-justify-between modern-items-center modern-flex-wrap modern-gap-3">
          <div>
            {editingRegionName ? (
              <div className="modern-flex modern-items-center modern-gap-2 modern-flex-wrap">
                <input
                  type="text"
                  value={newRegionName}
                  onChange={(e) => { setNewRegionName(e.target.value); setRegionNameError(''); }}
                  className="modern-input"
                  placeholder="Region Name"
                  disabled={savingRegionName}
                  style={{ minWidth: 240 }}
                />
                <button
                  type="button"
                  className="modern-button modern-button-success"
                  disabled={savingRegionName}
                  onClick={handleSaveRegionName}
                >
                  {savingRegionName ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="modern-button modern-button-secondary"
                  disabled={savingRegionName}
                  onClick={() => { setEditingRegionName(false); setRegionNameError(''); }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <h1 className="modern-title" style={{ marginBottom: 4 }}>{regionName || 'Region'}</h1>
            )}
            {regionNameError && <div className="modern-error" style={{ marginTop: 8 }}>{regionNameError}</div>}
            {clientName ? (
              <p className="modern-subtitle" style={{ marginBottom: 0 }}>{clientName}</p>
            ) : null}
          </div>
          <div className="modern-flex modern-items-center modern-gap-2 modern-flex-wrap">
            {!editingRegionName && (
              <button
                type="button"
                className="modern-button modern-button-secondary"
                onClick={() => {
                  setEditingRegionName(true);
                  setNewRegionName(regionName || '');
                  setRegionNameError('');
                }}
              >
                Rename Region
              </button>
            )}
            <button
              type="button"
              className="modern-button modern-button-secondary"
              onClick={() => navigate(`/region/${regionId}/access`, {
                state: { scope: 'region', clientId },
              })}
            >
              Add Region-Level Manager
            </button>
          </div>
        </div>
      </div>

      <div className="modern-flex modern-justify-between modern-items-center modern-mb-4">
        <h2 className="modern-heading" style={{ marginBottom: 0 }}>Teams</h2>
        <button
          type="button"
          className="modern-button modern-button-success"
          onClick={openAddTeamModal}
        >
          + Add Team
        </button>
      </div>

      {loading ? (
        <div className="modern-loading">Loading teams…</div>
      ) : error ? (
        <div className="modern-error">{error}</div>
      ) : teams.length === 0 ? (
        <div className="modern-list-panel">
          <div className="modern-list-empty">No teams in this region yet.</div>
        </div>
      ) : (
        <div className="modern-flex modern-flex-col modern-gap-4">
          {teams.map((team) => (
            <div key={team.id} className="modern-card">
              <div className="modern-flex modern-justify-between modern-items-center modern-mb-4">
                {editingTeamId === team.id ? (
                  <div className="modern-flex modern-items-center modern-gap-2 modern-flex-wrap">
                    <input
                      type="text"
                      className="modern-input"
                      value={newTeamNameEdit}
                      onChange={(e) => { setNewTeamNameEdit(e.target.value); setTeamNameError(''); }}
                      disabled={savingTeamName}
                      placeholder="Team Name"
                      style={{ minWidth: 220 }}
                    />
                    <button
                      type="button"
                      className="modern-button modern-button-success"
                      disabled={savingTeamName}
                      onClick={() => handleSaveTeamName(team.id)}
                    >
                      {savingTeamName ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="modern-button modern-button-secondary"
                      disabled={savingTeamName}
                      onClick={() => { setEditingTeamId(null); setTeamNameError(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <h3 className="modern-heading" style={{ marginBottom: 0 }}>{team.name || 'Untitled Team'}</h3>
                )}
                {editingTeamId !== team.id && (
                  <div className="modern-flex modern-items-center modern-gap-2">
                    <button
                      type="button"
                      className="modern-icon-button"
                      title="Rename team"
                      onClick={() => {
                        setEditingTeamId(team.id);
                        setNewTeamNameEdit(team.name || '');
                        setTeamNameError('');
                      }}
                    >
                      ✏️
                    </button>
                    <DeleteIconButton title="Delete team" onClick={() => setTeamToDelete(team.id)} />
                  </div>
                )}
              </div>
              {teamNameError && editingTeamId === team.id && (
                <div className="modern-error modern-mb-3">{teamNameError}</div>
              )}

              <div className="modern-flex modern-flex-wrap modern-gap-3 modern-mb-4">
                {(team.users || []).length === 0 ? (
                  <span style={{ color: '#94a3b8' }}>No users in this team.</span>
                ) : (
                  (team.users || []).map((user) => (
                    <div key={user.id} className="modern-user-tag" title={user.email || ''}>
                      {user.full_name || user.email}
                    </div>
                  ))
                )}
              </div>

              <div className="modern-flex modern-flex-wrap modern-gap-2">
                <button
                  type="button"
                  className="modern-button modern-button-warning"
                  onClick={() => navigate(`/team/${team.id}/edit-members`, {
                    state: {
                      clientId,
                      teamName: team.name,
                      team: {
                        id: team.id,
                        name: team.name,
                        client_id: team.client_id,
                        region_id: team.region_id,
                      },
                    },
                  })}
                >
                  Edit Team Membership
                </button>
                <button
                  type="button"
                  className="modern-button modern-button-info"
                  onClick={() => navigate(`/team/${team.id}/bootcamps`, {
                    state: {
                      teamName: team.name,
                      clientId,
                      regionId,
                    },
                  })}
                >
                  View All Bootcamps
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showTeamModal && (
        <div className="modern-modal-overlay">
          <div className="modern-modal">
            <button
              type="button"
              onClick={() => setShowTeamModal(false)}
              className="modern-modal-close"
            >
              &times;
            </button>
            <h2 className="modern-heading">Add Team</h2>
            <input
              type="text"
              value={newTeamName}
              onChange={(e) => { setNewTeamName(e.target.value); setTeamModalError(''); }}
              placeholder="Team Name"
              className="modern-input modern-mb-4"
              disabled={addingTeam}
            />
            {teamModalError && <div className="modern-error">{teamModalError}</div>}
            <button
              type="button"
              onClick={handleAddTeam}
              disabled={addingTeam}
              className="modern-button"
              style={{ width: '100%' }}
            >
              {addingTeam ? 'Adding…' : 'Add Team'}
            </button>
          </div>
        </div>
      )}

      {teamToDelete && (
        <div className="modern-modal-overlay">
          <div className="modern-modal">
            <button
              type="button"
              onClick={() => setTeamToDelete(null)}
              className="modern-modal-close"
            >
              &times;
            </button>
            <h2 className="modern-heading">Delete this team?</h2>
            <p style={{ color: '#64748b', marginBottom: 24 }}>
              Past bootcamps completed by this team will still be visible.
            </p>
            <div className="modern-flex modern-gap-4">
              <button
                type="button"
                className="modern-button modern-button-danger"
                onClick={confirmDeleteTeam}
                disabled={deletingTeam}
              >
                {deletingTeam ? 'Deleting…' : 'Delete Team'}
              </button>
              <button
                type="button"
                className="modern-button modern-button-secondary"
                onClick={() => setTeamToDelete(null)}
                disabled={deletingTeam}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRegionPage;
